/**
 * Debounce utility function
 * Delays function execution until after wait milliseconds have elapsed since the last time it was invoked
 */

export type DebouncedFunction<T extends (...args: any[]) => any> = {
  (...args: Parameters<T>): void;
  cancel: () => void;
  flush: () => void;
};

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds
 * have elapsed since the last time the debounced function was invoked.
 * 
 * @param func - The function to debounce
 * @param wait - The number of milliseconds to delay
 * @param immediate - If true, trigger the function on the leading edge instead of trailing
 * @returns A debounced version of the function with cancel and flush methods
 * 
 * @example
 * ```javascript
 * import { debounce } from '@smarterservices/smarter-elements';
 * 
 * const debouncedResize = debounce(() => {
 *   element.recalculateSize();
 * }, 300);
 * 
 * window.addEventListener('resize', debouncedResize);
 * 
 * // Cancel pending execution
 * debouncedResize.cancel();
 * 
 * // Execute immediately
 * debouncedResize.flush();
 * ```
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate: boolean = false
): DebouncedFunction<T> {
  let timeout: number | null = null;
  let args: Parameters<T> | null = null;
  let context: any = null;

  const later = () => {
    timeout = null;
    if (!immediate && args) {
      func.apply(context, args);
      context = args = null;
    }
  };

  const debounced = function(this: any, ...newArgs: Parameters<T>) {
    context = this;
    args = newArgs;
    
    const callNow = immediate && !timeout;
    
    if (timeout) {
      clearTimeout(timeout);
    }
    
    timeout = window.setTimeout(later, wait);
    
    if (callNow) {
      func.apply(context, args);
      context = args = null;
    }
  } as DebouncedFunction<T>;

  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
    context = args = null;
  };

  debounced.flush = () => {
    if (timeout && args) {
      clearTimeout(timeout);
      func.apply(context, args);
      timeout = null;
      context = args = null;
    }
  };

  return debounced;
}

/**
 * Creates a throttled function that only invokes func at most once per every wait milliseconds
 * 
 * @param func - The function to throttle
 * @param wait - The number of milliseconds to throttle invocations to
 * @param options - Options object
 * @returns A throttled version of the function
 * 
 * @example
 * ```javascript
 * import { throttle } from '@smarterservices/smarter-elements';
 * 
 * const throttledScroll = throttle(() => {
 *   console.log('Scroll event');
 * }, 100);
 * 
 * window.addEventListener('scroll', throttledScroll);
 * ```
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  options: { leading?: boolean; trailing?: boolean } = {}
): DebouncedFunction<T> {
  let timeout: number | null = null;
  let previous = 0;
  let args: Parameters<T> | null = null;
  let context: any = null;

  const { leading = true, trailing = true } = options;

  const later = () => {
    previous = leading === false ? 0 : Date.now();
    timeout = null;
    if (args) {
      func.apply(context, args);
      context = args = null;
    }
  };

  const throttled = function(this: any, ...newArgs: Parameters<T>) {
    const now = Date.now();
    if (!previous && leading === false) previous = now;
    
    const remaining = wait - (now - previous);
    context = this;
    args = newArgs;
    
    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      previous = now;
      func.apply(context, args);
      context = args = null;
    } else if (!timeout && trailing !== false) {
      timeout = window.setTimeout(later, remaining);
    }
  } as DebouncedFunction<T>;

  throttled.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
    previous = 0;
    context = args = null;
  };

  throttled.flush = () => {
    if (timeout && args) {
      clearTimeout(timeout);
      func.apply(context, args);
      timeout = null;
      previous = Date.now();
      context = args = null;
    }
  };

  return throttled;
}
